import React from "react";
import { ContainerComponent, wrap, Wrapper } from "AppReact";
import {
  Icon,
  SecondaryTextSm,
  BorderlessKeyInput,
  SecondaryTextXs,
} from "atoms";
import {
  P2,
  Mx2,
  Mr2,
  StaticWrapper,
  RelativeWrapper,
  FullWidthFlexCenterWrapper,
  WhiteSpaceNoWrap,
  FullWidthRowCenterScrollWrapper,
  FlexCenterJustifyCenterWrapper,
  TagRowsWrapper,
  BackgroundLightPrimaryWrapper,
  OpacityWrapper,
  RotateUpOrDownWrapper,
} from "wrappers";
import Controller from "./controller";
import internalState from "./internalState";
import { DownCaretSvg, PlusSignSvg } from "components/icons";
import labelSwitch from "./labelSwitch";
import { escapedRegex } from "utils";
import { TextButton, DropdownColoredTextButton } from "molecules";
import { ButtonsWrapper } from "./../customDropdownV2/wrappers";
import { BodyWrapper, HeaderWrapper } from "../customDropdown/wrappers";
import TagRowV2 from "./../tagRowV2";
import styled from "styled-components";

const headerRef = React.createRef();

const contentElements = [
  {
    wrapper: HeaderWrapper,
    name: "Selector",
    ref: headerRef,
    isButton: true,
    displayState: ({ borderColor }) => ({ borderColor }),
    bindings: ({
      isOpen,
      multipleSelect,
      selectedOptionIds,
      options,
      inputRef,
      containerRef,
      disabled,
    }) => ({
      isOpen,
      containerRef,
      multipleSelect,
      selectedOptionIds,
      options,
      inputRef,
      disabled,
    }),
    elements: [
      {
        wrapper: wrap(FullWidthFlexCenterWrapper, Wrapper(styled.div`width: 270px;  overflow: auto; flex: 1;`)),
        name: "LeftSide",
        elements: [
          {
            componentSelect: labelSwitch,
            name: "Label",
            displayValue: ({ label }) => label.values[0],
            renderIf: ({ label }) => !!label?.component,
            bindings: ({ label }) => {
              if (label.keys?.length > 0) {
                const combinedValues = { ...label };
                label.keys.forEach((key, index) => {
                  combinedValues[key] = label.values[index];
                });
                return combinedValues;
              }
              return label;
            },
          },
          {
            wrapper: FullWidthRowCenterScrollWrapper,
            name: "Wrapper",
            ref: ({ inputRef }) => inputRef ?? null,
            elements: [
              {
                component: DropdownColoredTextButton,
                name: "SelectedButton",
                renderIf: ({ multipleSelect, isOpen }) => multipleSelect,
                isListOf: "selectedOptionIds",
                bindings: ({
                  selectedOptionId,
                  selectedOptionIds,
                  options,
                }) => {
                  const selectedTag = options.find(
                    (o) => o.id === selectedOptionId
                  );
                  return {
                    text: selectedTag?.display ?? "",
                    color: selectedTag?.color,
                    selectedOptionId,
                    selectedOptionIds,
                  };
                },
              },
              {
                component: wrap(wrap(SecondaryTextXs, WhiteSpaceNoWrap), Mx2),
                name: "SelectedValue",
                renderIf: ({
                  personSelector,
                  multipleSelect,
                  selectedOptionIds,
                  isOpen,
                }) =>
                  !!personSelector &&
                  !isOpen &&
                  !multipleSelect &&
                  selectedOptionIds.length > 0,
                displayValue: ({ options, selectedOptionIds }) => {
                  let option = options.find(
                    (o) => o.id === selectedOptionIds[0]
                  );
                  return option?.display ?? "";
                },
              },
              {
                component: wrap(wrap(SecondaryTextSm, WhiteSpaceNoWrap), Mx2),
                name: "SelectedValue",
                renderIf: ({
                  personSelector,
                  multipleSelect,
                  selectedOptionIds,
                  isOpen,
                }) =>
                  !personSelector &&
                  !isOpen &&
                  !multipleSelect &&
                  selectedOptionIds.length > 0,
                displayValue: ({ options, selectedOptionIds }) => {
                  let option = options.find(
                    (o) => o.id === selectedOptionIds[0]
                  );
                  return option?.display ?? "";
                },
              },
              {
                component: BorderlessKeyInput,
                name: "SearchInput",
                renderIf: ({ hasSearch }) => hasSearch,
                displayState: ({ padding, fontSize }) => ({
                  padding,
                  fontSize,
                }),
                bindings: ({
                  searchTerm,
                  placeholder,
                  disabled,
                  isOpen,
                  selectedOptionIds,
                }) => ({
                  value: searchTerm,
                  disabled,
                  placeholder:
                    isOpen || selectedOptionIds.length === 0
                      ? placeholder ?? "Search..."
                      : "",
                }),
              },
            ],
          },
        ],
      },
      {
        wrapper: ButtonsWrapper,
        name: "ButtonsWrapper",
        displayState: ({ personSelector }) => ({ personSelector}),
        elements: [
          {
            wrapper: FlexCenterJustifyCenterWrapper,
            name: "AddButtonWrapper",
            elements: [
              {
                wrapper: OpacityWrapper,
                name: "IconWrapper",
                displayState: () => ({ opacity: '1' }),
                elements: [
                  {
                    component: wrap(Icon, Mr2),
                    name: "PlusSignSvg",
                    displayValue: () => PlusSignSvg({}),
                    bindings: () => ({}),
                  },
                ]
              },
              {
                component: TextButton,
                name: "AddNew",
                renderIf: ({ showAddButton }) => !!showAddButton,
                displayState: () => ({ small: true }),
                bindings: ({ textButtonFontWeight, action, searchTerm }) => ({
                  fontWeight: textButtonFontWeight,
                  title: "Add New",
                  small: true,
                  action,
                  searchTerm,
                }),
              },
            ],
          },
          {
            wrapper: RotateUpOrDownWrapper,
            name: "CaretWrapper",
            displayState: ({ isOpen }) => ({ direction: isOpen ? "up" : "down" }),
            elements:[
              {
                component: Icon,
                name: "DownCaretSvg",
                displayValue: () => DownCaretSvg({}),
                bindings: () => ({}),
              },
            ]
          },
        ],
      },
    ],
  },
  {
    wrapper: BodyWrapper,
    name: "Body",
    displayState: ({ openOnTop, left, bodyWidth }) => ({
      width: bodyWidth ?? headerRef?.current?.offsetWidth,
      openOnTop,
      left,
    }),
    renderIf: ({ isOpen }) => isOpen,
    elements: [
      {
        wrapper: TagRowsWrapper,
        name: "TagsWrapper",
        ref: ({ tagInputRef }) => tagInputRef ?? null,
        elements: [
          {
            component: wrap(wrap(TagRowV2, P2), BackgroundLightPrimaryWrapper),
            name: "TagRow",
            isListOf: {
              option: ({ options }) => {
                return options ?? [];
              },
            },
            renderIf: ({ options, option, searchTerm, selectedOptionIds }) => {
              return (
                escapedRegex(searchTerm).test(option.display) &&
                !selectedOptionIds.includes(option.id)
              );
            },
            bindings: ({ option, optionIndex, tagInputRef }) => {
              return {
                tagInputRef,
                tagIndex: optionIndex,
                tag: option,
                maxLength: 20,
              };
            },
          },
        ],
      },
    ],
  },
];

export const staticDefinition = {
  name: "Dropdown",
  controller: Controller,
  initialProps: ({
    isOpen = false,
    action = "add_new",
    fontSize = "0.875rem",
    personSelector = false,
    showAddButton = false,
    textButtonFontWeight,
    options,
    selectedOptionIds,
    label,
    multipleSelect,
    hasSearch,
    placeholder,
    openOnTop,
    scrollLeft,
    bodyWidth,
    borderColor,
    disabled,
    padding,
  }) => ({
    isOpen,
    action,
    fontSize,
    personSelector,
    showAddButton,
    textButtonFontWeight,
    options,
    selectedOptionIds,
    bodyWidth,
    label,
    multipleSelect,
    hasSearch,
    placeholder,
    openOnTop,
    borderColor,
    padding,
    disabled,
    left: scrollLeft ? (headerRef?.current?.offsetLeft ?? 0) - scrollLeft : 0,
  }),
  props: internalState,
  elements: [
    {
      wrapper: StaticWrapper,
      name: "__transparent__",
      ref: ({ containerRef }) => containerRef,
      elements: contentElements,
    },
  ],
};

export const relativeDefinition = {
  name: "Dropdown",
  controller: Controller,
  initialProps: ({
    isOpen = false,
    action = "add_new",
    fontSize = "0.875rem",
    personSelector = false,
    showAddButton = false,
    textButtonFontWeight,
    options,
    selectedOptionIds,
    label,
    multipleSelect,
    hasSearch,
    placeholder,
    openOnTop,
    scrollLeft,
    bodyWidth,
    borderColor,
    disabled,
    padding,
  }) => ({
    isOpen,
    action,
    fontSize,
    showAddButton,
    personSelector,
    textButtonFontWeight,
    options,
    selectedOptionIds,
    bodyWidth,
    label,
    multipleSelect,
    hasSearch,
    placeholder,
    openOnTop,
    borderColor,
    padding,
    disabled,
    left: scrollLeft ? (headerRef?.current?.offsetLeft ?? 0) - scrollLeft : 0,
  }),
  props: internalState,
  elements: [
    {
      wrapper: RelativeWrapper,
      name: "__transparent__",
      ref: ({ containerRef }) => containerRef,
      elements: contentElements,
    },
  ],
};

export const CustomDropdownWithStaticWrapper =
  ContainerComponent(staticDefinition);

export default ContainerComponent(relativeDefinition);
